import { useState } from 'react';
import { Plus } from 'lucide-react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useForumPublish } from '@/hooks/useForum';
import { genId } from '@/lib/forum';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export function NewForumDialog({
  categoryId,
  triggerLabel = 'New forum',
}: {
  categoryId: string;
  triggerLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const { user } = useCurrentUser();
  const { publishNode } = useForumPublish();
  const { toast } = useToast();

  if (!user) return null;

  async function submit() {
    if (!title.trim()) {
      toast({ title: 'A title is required' });
      return;
    }
    setBusy(true);
    try {
      await publishNode({
        id: genId('forum'),
        parent: categoryId,
        title: title.trim(),
        content: desc.trim(),
        type: 'forum',
      });
      toast({ title: 'Forum created' });
      setOpen(false);
      setTitle('');
      setDesc('');
    } catch (e) {
      toast({ title: 'Could not create forum', description: String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
        >
          <Plus className="h-3.5 w-3.5" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a forum</DialogTitle>
          <DialogDescription>Forums contain topics and discussions.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="forum-title">Title</Label>
            <Input
              id="forum-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Introduce Yourself"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="forum-desc">Description (optional)</Label>
            <Textarea
              id="forum-desc"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="What is this forum about?"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            className="bg-[#105B86] text-white hover:bg-[#0d4f74]"
            onClick={submit}
            disabled={busy}
          >
            {busy ? 'Creating…' : 'Create forum'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
